export const HEADSHOT_MAX_EDGE = 800
export const HEADSHOT_JPEG_QUALITY = 0.82

// JPEG and PNG only. WebP is rejected. The canvas below always re-encodes JPEG.
export const HEADSHOT_FILE_ACCEPT = '.jpg,.jpeg,.png,image/jpeg,image/png'

export function acceptedHeadshotFile(file: { name: string; type: string }): boolean {
  const type = file.type.toLowerCase()
  if (type === 'image/jpeg' || type === 'image/png') return true
  // A missing type happens for some OS file pickers. Trust a jpeg or png name then.
  if (type && type !== 'application/octet-stream') return false

  const name = file.name.toLowerCase()
  return name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png')
}

export function headshotPixelSize(
  width: number,
  height: number,
  maxEdge = HEADSHOT_MAX_EDGE,
): { width: number; height: number } {
  const longest = Math.max(width, height)
  if (longest <= maxEdge || longest === 0) return { width, height }

  const scale = maxEdge / longest
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

// Re-encodes every photo. A file can be small in pixels and still be many megabytes.
export async function resizeHeadshot(file: File): Promise<File> {
  if (!acceptedHeadshotFile(file)) throw new Error('Please choose a JPEG or PNG photo.')

  const bitmap = await createImageBitmap(file)
  try {
    const size = headshotPixelSize(bitmap.width, bitmap.height)
    const canvas = document.createElement('canvas')
    canvas.width = size.width
    canvas.height = size.height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Could not prepare the photo')

    context.drawImage(bitmap, 0, 0, size.width, size.height)
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', HEADSHOT_JPEG_QUALITY)
    })
    if (!blob) throw new Error('Could not prepare the photo')

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'headshot'
    return new File([blob], `${baseName}.jpeg`, { type: 'image/jpeg' })
  } finally {
    bitmap.close()
  }
}
